import { useState, useRef, useEffect } from 'react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { getCroppedImg } from '../utils/cropUtils';

interface CropModalProps {
  imageSrc: string;
  onClose: () => void;
  onCropComplete: (croppedBlob: Blob) => void;
}

interface AspectOption {
  label: string;
  value: number | undefined;
  icon: string;
}

const ASPECT_RATIOS: AspectOption[] = [
  { label: 'Free', value: undefined, icon: '↔️' },
  { label: 'Square', value: 1, icon: '⬜' },
  { label: '16:9', value: 16 / 9, icon: '📺' },
  { label: '4:5', value: 4 / 5, icon: '📱' },
  { label: '3:2', value: 3 / 2, icon: '📷' },
];

export default function CropModal({ imageSrc, onClose, onCropComplete }: CropModalProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    x: 10,
    y: 10,
    width: 80,
    height: 80,
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [selectedAspect, setSelectedAspect] = useState<number | undefined>(undefined);
  const [isProcessing, setIsProcessing] = useState(false);

  // Reset crop when aspect ratio changes to recenter
  useEffect(() => {
    if (imgRef.current) {
      // Use naturalWidth/Height to match actual image dimensions
      const { naturalWidth, naturalHeight } = imgRef.current;
      if (naturalWidth && naturalHeight) {
        const cropWidth = Math.min(naturalWidth * 0.8, naturalWidth);
        const cropHeight = Math.min(naturalHeight * 0.8, naturalHeight);
        
        setCrop({
          unit: 'px',
          x: (naturalWidth - cropWidth) / 2,
          y: (naturalHeight - cropHeight) / 2,
          width: cropWidth,
          height: cropHeight,
        });
      }
    }
  }, [selectedAspect]);

  const handleApplyCrop = async () => {
    if (!completedCrop || !imgRef.current) return;

    setIsProcessing(true);
    try {
      const croppedBlob = await getCroppedImg(
        imgRef.current,
        completedCrop,
        0.95
      );
      onCropComplete(croppedBlob);
    } catch (error) {
      console.error('Failed to crop image:', error);
      alert('Failed to crop image. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black z-[100] flex flex-col">
      {/* Header */}
      <div className="bg-gray-900 text-white px-4 py-3 flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <span>✂️</span>
          <span>Crop Photo</span>
        </h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-colors p-1"
          aria-label="Close"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Cropper Area */}
      <div className="flex-1 relative bg-black flex items-center justify-center overflow-auto">
        <div className="max-w-full max-h-full p-4">
          <ReactCrop
            crop={crop}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={selectedAspect}
            className="max-w-full"
          >
            <img
              ref={imgRef}
              src={imageSrc}
              alt="Crop preview"
              className="max-w-full max-h-[calc(100vh-300px)] object-contain"
              onLoad={(e) => {
                // Set initial crop using NATURAL image dimensions (not displayed size)
                // This is critical: CSS may scale the displayed image, but crop coordinates
                // must match the actual pixel dimensions of the source image
                const { naturalWidth, naturalHeight } = e.currentTarget;
                const cropWidth = Math.min(naturalWidth * 0.8, naturalWidth);
                const cropHeight = Math.min(naturalHeight * 0.8, naturalHeight);
                
                setCrop({
                  unit: 'px',
                  x: (naturalWidth - cropWidth) / 2,
                  y: (naturalHeight - cropHeight) / 2,
                  width: cropWidth,
                  height: cropHeight,
                });
              }}
            />
          </ReactCrop>
        </div>
      </div>

      {/* Helper Text */}
      {selectedAspect === undefined && (
        <div className="bg-blue-900/50 text-blue-200 px-4 py-2 text-sm border-t border-blue-700">
          <div className="flex items-center gap-2">
            <span>💡</span>
            <span><strong>Free Crop:</strong> Drag corners to resize freely. Drag edges to adjust width/height. Drag center to move.</span>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="bg-gray-900 text-white p-4 space-y-4">
        {/* Aspect Ratio */}
        <div>
          <label className="text-xs font-medium text-gray-400 mb-2 block">
            Aspect Ratio
          </label>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {ASPECT_RATIOS.map((option) => (
              <button
                key={option.label}
                onClick={() => setSelectedAspect(option.value)}
                className={`flex-shrink-0 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedAspect === option.value
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                <span className="mr-1">{option.icon}</span>
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="flex-1 px-4 py-3 bg-gray-800 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleApplyCrop}
            disabled={isProcessing || !completedCrop}
            className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isProcessing ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                <span>Processing...</span>
              </>
            ) : (
              <>
                <span>✓</span>
                <span>Apply Crop</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

